// AuditTab — Phase 5 Task 14 port of docs/mockups/leaderboard-detail.html
// states starting at L2779 (default). Filter bar at L2870, table thead at
// L2900.
//
// Backend constraints (Go-truth):
//   - listAudit requires cycleId (server returns 400 if omitted).
//   - Server-side filters: ?entity, ?reject_reason, ?cursor, ?limit.
//   - There is NO channel / status / range query param on the backend, so
//     those filters are evaluated client-side over the fetched page.
//
// Pagination via `cursor` is intentionally deferred for T14; the cursor
// `<button>` from the mockup is not rendered. "Custom…" range option is
// rendered but inert (defer).

import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { Alert } from "../../../components/Alert/Alert";
import { Select } from "../../../components/Select/Select";
import { Skeleton } from "../../../components/Skeleton/Skeleton";
import { ApiError, describeError } from "../../../lib/errors";
import { formatRelativeShort } from "../../../lib/format";
import { lbKeys, listAudit, listCycles } from "../api";
import type { AuditChannel, AuditEntry, Cycle } from "../types";
import styles from "./AuditTab.module.css";

// ──────────────────────────────────────────────────────────────────────────────
// Formatters
// ──────────────────────────────────────────────────────────────────────────────

const ABS_FMT = new Intl.DateTimeFormat("en-US", {
  year: "numeric",
  month: "short",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hour12: false,
});

function fmtNumber(n: number | undefined | null): string {
  if (n === undefined || n === null || Number.isNaN(n)) return "—";
  return n.toLocaleString("en-US");
}

function fmtAbs(iso: string | undefined | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return ABS_FMT.format(d);
}

function shortId(id: string | undefined | null, n = 8): string {
  if (!id) return "—";
  return id.replace(/-/g, "").slice(0, n);
}

function describeQueryError(error: unknown): string {
  if (error instanceof ApiError) return describeError(error.code);
  return describeError("internal");
}

// ──────────────────────────────────────────────────────────────────────────────
// Filter types & helpers
// ──────────────────────────────────────────────────────────────────────────────

type StatusSeg = "applied" | "rejected" | "all";
type RangeKind = "24h" | "7d" | "30d" | "custom";

const RANGE_HOURS: Record<Exclude<RangeKind, "custom">, number> = {
  "24h": 24,
  "7d": 24 * 7,
  "30d": 24 * 30,
};

function isRejected(row: AuditEntry): boolean {
  // Go-truth: applied_score absent (null/undefined) OR a reject_reason set
  // means the submit was rejected by the guard pipeline.
  return (
    row.applied_score === undefined ||
    row.applied_score === null ||
    !!row.reject_reason
  );
}

function withinRange(iso: string, kind: RangeKind): boolean {
  if (kind === "custom") return true; // deferred — treat as no-op (show all)
  const hours = RANGE_HOURS[kind];
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return false;
  const cutoff = Date.now() - hours * 3600 * 1000;
  return d.getTime() >= cutoff;
}

// ──────────────────────────────────────────────────────────────────────────────
// Debounce primitive (300ms for the Entity uuid input)
// ──────────────────────────────────────────────────────────────────────────────

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setV(value), ms);
    return () => window.clearTimeout(id);
  }, [value, ms]);
  return v;
}

// ──────────────────────────────────────────────────────────────────────────────
// AuditTab — entry point
// ──────────────────────────────────────────────────────────────────────────────

interface Props {
  gameId: string;
  lbId: string;
}

const ALL_CHANNELS: AuditChannel[] = ["client", "s2s", "admin"];

export function AuditTab({ gameId, lbId }: Props) {
  // === Cycles =====================================================
  const cyclesQ = useQuery({
    queryKey: lbKeys.cycles(gameId, lbId),
    queryFn: () => listCycles(gameId, lbId),
    enabled: !!gameId && !!lbId,
  });
  const cycles: Cycle[] = cyclesQ.data?.cycles ?? [];

  const [selectedCycleId, setSelectedCycleId] = useState<string>("");
  useEffect(() => {
    if (selectedCycleId) return;
    if (cycles.length === 0) return;
    const open = cycles.find((c) => c.status === "open");
    setSelectedCycleId(open?.id ?? cycles[0]?.id ?? "");
  }, [cycles, selectedCycleId]);

  // === Filter state =============================================== //
  const [channels, setChannels] = useState<Set<AuditChannel>>(
    () => new Set(ALL_CHANNELS),
  );
  const [status, setStatus] = useState<StatusSeg>("applied");
  const [range, setRange] = useState<RangeKind>("7d");
  const [entityRaw, setEntityRaw] = useState("");
  const entity = useDebounced(entityRaw.trim(), 300);

  // === Server filter (only entity ships to backend) =============== //
  const serverFilter = useMemo(
    () => ({ entity_id: entity || undefined, limit: 100 }),
    [entity],
  );

  const auditQ = useQuery({
    queryKey: lbKeys.audit(gameId, lbId, selectedCycleId, serverFilter),
    queryFn: () => listAudit(gameId, lbId, selectedCycleId, serverFilter),
    enabled: !!selectedCycleId,
  });

  const allRows: AuditEntry[] = auditQ.data?.audit ?? [];

  // === Client-side filter pass over the fetched page ============== //
  const rows = useMemo(() => {
    return allRows.filter((row) => {
      if (!channels.has(row.channel)) return false;
      const rejected = isRejected(row);
      if (status === "applied" && rejected) return false;
      if (status === "rejected" && !rejected) return false;
      if (!withinRange(row.created_at, range)) return false;
      return true;
    });
  }, [allRows, channels, status, range]);

  // === Row expansion ============================================== //
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const toggleExpand = (id: number) =>
    setExpandedId((cur) => (cur === id ? null : id));

  // === Channel chip toggle ======================================== //
  const toggleChannel = (ch: AuditChannel) => {
    setChannels((prev) => {
      const next = new Set(prev);
      if (next.has(ch)) next.delete(ch);
      else next.add(ch);
      return next;
    });
  };

  // === Cycles loading / error / empty ============================= //

  if (cyclesQ.isError) {
    return (
      <Alert variant="danger" title="Couldn't load cycles">
        {describeQueryError(cyclesQ.error)}
      </Alert>
    );
  }

  if (cyclesQ.isLoading) {
    return (
      <div className={styles.card}>
        <Skeleton height={240} />
      </div>
    );
  }

  if (cycles.length === 0) {
    return (
      <div className={styles.card}>
        <div className={styles.emptyState}>
          <span className={styles.emptyIcon} aria-hidden="true">
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M12 9v4M12 17h.01" />
              <circle cx="12" cy="12" r="10" />
            </svg>
          </span>
          <h3>No cycles yet</h3>
          <p>
            Submit some scores to populate the audit log. The audit table is
            scoped per cycle, so the first cycle must exist before events can
            be displayed here.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.wrap}>
      <FilterBar
        cycles={cycles}
        selectedCycleId={selectedCycleId}
        onSelectCycle={setSelectedCycleId}
        channels={channels}
        onToggleChannel={toggleChannel}
        status={status}
        onChangeStatus={setStatus}
        range={range}
        onChangeRange={setRange}
        entityRaw={entityRaw}
        onChangeEntity={setEntityRaw}
        counts={{ shown: rows.length, total: allRows.length }}
      />

      {auditQ.isError ? (
        <div className={styles.card}>
          <div className={styles.cardError}>
            Couldn&apos;t load audit — {describeQueryError(auditQ.error)}
          </div>
        </div>
      ) : auditQ.isLoading ? (
        <div className={styles.card}>
          <Skeleton height={240} />
        </div>
      ) : rows.length === 0 ? (
        <div className={styles.card}>
          <div className={styles.placeholder}>
            {allRows.length === 0
              ? "No audit events for this cycle yet."
              : "No events match the current filters."}
          </div>
        </div>
      ) : (
        <AuditTable
          rows={rows}
          expandedId={expandedId}
          onToggleExpand={toggleExpand}
        />
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Filter bar
// ──────────────────────────────────────────────────────────────────────────────

interface FilterBarProps {
  cycles: Cycle[];
  selectedCycleId: string;
  onSelectCycle: (id: string) => void;
  channels: Set<AuditChannel>;
  onToggleChannel: (ch: AuditChannel) => void;
  status: StatusSeg;
  onChangeStatus: (s: StatusSeg) => void;
  range: RangeKind;
  onChangeRange: (r: RangeKind) => void;
  entityRaw: string;
  onChangeEntity: (v: string) => void;
  counts: { shown: number; total: number };
}

function FilterBar({
  cycles,
  selectedCycleId,
  onSelectCycle,
  channels,
  onToggleChannel,
  status,
  onChangeStatus,
  range,
  onChangeRange,
  entityRaw,
  onChangeEntity,
  counts,
}: FilterBarProps) {
  return (
    <div className={styles.filters} role="region" aria-label="Audit filters">
      <span className={styles.fLabel}>Cycle</span>
      <div className={styles.cycleSelect}>
        <Select
          value={selectedCycleId}
          onChange={onSelectCycle}
          options={cycles.map((c) => ({
            value: c.id,
            label: `${shortId(c.id)} · ${c.status}`,
          }))}
          monospaceValue
        />
      </div>

      <span className={styles.fLabel}>Channel</span>
      <div className={styles.chipGroup} role="group" aria-label="Channel">
        {ALL_CHANNELS.map((ch) => {
          const active = channels.has(ch);
          return (
            <button
              key={ch}
              type="button"
              className={`${styles.chipTog} ${active ? styles.chipTogActive : ""}`}
              aria-label={ch}
              aria-pressed={active}
              onClick={() => onToggleChannel(ch)}
            >
              <span className={styles.chipDot} aria-hidden="true" />
              {ch}
            </button>
          );
        })}
      </div>

      <span className={styles.fLabel}>Status</span>
      <div className={styles.seg} role="group" aria-label="Status">
        <SegOpt label="Applied" value="applied" current={status} onSelect={onChangeStatus} />
        <SegOpt label="Rejected" value="rejected" current={status} onSelect={onChangeStatus} />
        <SegOpt label="All" value="all" current={status} onSelect={onChangeStatus} />
      </div>

      <span className={styles.fLabel}>Range</span>
      <div className={styles.rangeSelect}>
        <Select
          value={range}
          onChange={(v) => onChangeRange(v as RangeKind)}
          // TODO(audit-range-custom): wire a real date-range picker. For now
          // selecting "Custom…" is a no-op; we keep the option so the UI
          // matches the mockup, but withinRange("custom") returns true.
          options={[
            { value: "7d", label: "Last 7 days" },
            { value: "24h", label: "Last 24 hours" },
            { value: "30d", label: "Last 30 days" },
            { value: "custom", label: "Custom…" },
          ]}
        />
      </div>

      <span className={styles.fLabel}>Entity</span>
      <input
        className={`${styles.input} ${styles.entityInput}`}
        type="search"
        aria-label="Entity"
        placeholder="entity_id (uuid)"
        title="Filter by entity_id — maps to ?entity=<uuid>"
        value={entityRaw}
        onChange={(e) => onChangeEntity(e.target.value)}
      />

      <span className={styles.spacer} />
      <span className={styles.helper}>
        {counts.shown} of {counts.total} events
      </span>
    </div>
  );
}

function SegOpt({
  label,
  value,
  current,
  onSelect,
}: {
  label: string;
  value: StatusSeg;
  current: StatusSeg;
  onSelect: (v: StatusSeg) => void;
}) {
  const active = current === value;
  return (
    <button
      type="button"
      className={`${styles.segOpt} ${active ? styles.segOptActive : ""}`}
      aria-pressed={active}
      onClick={() => onSelect(value)}
    >
      {label}
    </button>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Table
// ──────────────────────────────────────────────────────────────────────────────

function AuditTable({
  rows,
  expandedId,
  onToggleExpand,
}: {
  rows: AuditEntry[];
  expandedId: number | null;
  onToggleExpand: (id: number) => void;
}) {
  return (
    <div className={styles.card}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th style={{ width: 100 }}>When</th>
            <th style={{ width: 70 }}>Channel</th>
            <th style={{ width: 160 }}>Actor</th>
            <th style={{ width: 140 }}>Entity</th>
            <th style={{ width: 150 }}>Score</th>
            <th>Reject reason</th>
            <th style={{ width: 110 }}>Request ID</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <AuditRowGroup
              key={row.id}
              row={row}
              expanded={expandedId === row.id}
              onToggle={() => onToggleExpand(row.id)}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AuditRowGroup({
  row,
  expanded,
  onToggle,
}: {
  row: AuditEntry;
  expanded: boolean;
  onToggle: () => void;
}) {
  const rejected = isRejected(row);
  const actorCell =
    row.channel === "admin" && row.admin_user_id ? (
      <span className={styles.monoCell}>{shortId(row.admin_user_id)}</span>
    ) : (
      <span className={styles.muted}>—</span>
    );

  return (
    <>
      <tr
        className={`${styles.auditRow} ${expanded ? styles.auditRowExpanded : ""}`}
        onClick={onToggle}
        aria-expanded={expanded}
      >
        <td className={styles.muted} title={fmtAbs(row.created_at)}>
          {formatRelativeShort(row.created_at)}
        </td>
        <td>
          <ChannelBadge channel={row.channel} />
        </td>
        <td>{actorCell}</td>
        {/* TODO(audit-enrich): resolve display name for entity_id once the
            backend supports ?expand=entity for /audit. Until then we show
            a stable shortId mono cell. */}
        <td className={styles.monoCell}>{shortId(row.entity_id)}</td>
        <td>
          <span
            className={`${styles.scoreFlow} ${rejected ? styles.scoreRejected : ""}`}
            title={row.reject_reason}
          >
            <span>{fmtNumber(row.submitted_score)}</span>
            <span className={styles.scoreArrow}>→</span>
            {rejected ? (
              <span className={styles.scoreX}>✗</span>
            ) : (
              <span>{fmtNumber(row.applied_score ?? null)}</span>
            )}
          </span>
        </td>
        <td>
          {row.reject_reason ? (
            <span className={styles.rejectReason} title={row.reject_reason}>
              {row.reject_reason}
            </span>
          ) : null}
        </td>
        <td>
          <span className={styles.reqIdCell}>{shortId(row.request_id)}</span>
        </td>
      </tr>
      {expanded && (
        <tr className={styles.expansionRow}>
          <td colSpan={7}>
            <ExpansionDetails row={row} />
          </td>
        </tr>
      )}
    </>
  );
}

function ExpansionDetails({ row }: { row: AuditEntry }) {
  return (
    <div className={styles.expansionBox}>
      <DetailItem label="Request ID" value={<code>{row.request_id}</code>} />
      <DetailItem label="Entity ID" value={<code>{row.entity_id}</code>} />
      <DetailItem
        label="Created at"
        value={<span>{fmtAbs(row.created_at)}</span>}
      />
      <DetailItem
        label="Cycle ID"
        value={<code>{shortId(row.cycle_id)}…</code>}
      />
      {row.channel === "admin" && row.admin_user_id && (
        <DetailItem
          label="Admin user"
          value={<code>{row.admin_user_id}</code>}
        />
      )}
      {row.reject_reason && (
        <DetailItem
          label="Reject reason"
          value={<span className={styles.danger}>{row.reject_reason}</span>}
        />
      )}
    </div>
  );
}

function DetailItem({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className={styles.detailItem}>
      <span className={styles.detailLabel}>{label}</span>
      <span className={styles.detailValue}>{value}</span>
    </div>
  );
}

function ChannelBadge({ channel }: { channel: AuditChannel }) {
  const cls =
    channel === "client"
      ? styles.chClient
      : channel === "s2s"
        ? styles.chS2s
        : styles.chAdmin;
  return <span className={`${styles.chBadge} ${cls}`}>{channel}</span>;
}
